import DateTimePicker from './DateTimePicker';

/** The app's standard calendar on a 'YYYY-MM-DD' string.
 *
 *  Replaces `<input type="date">`, whose picker is the browser's own: it
 *  carries a Clear link that empties the field, and Win the Day has no meaning
 *  without a day -- clearing it left the page reading "Could not load Win the
 *  Day". A cleared value is therefore ignored here rather than propagated.
 *
 *  Dates are built and read at noon. Parsing 'YYYY-MM-DD' as a bare date gives
 *  UTC midnight, which in any US timezone is the previous day once rendered
 *  locally, so the picker would open a day behind the value it was given.
 */
export function IsoDatePicker({ value, onChange, label, ariaLabel, className = '' }: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const asDate = (iso: string) => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!parts) return undefined;
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 12);
  };
  const asIso = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const picker = (
    <DateTimePicker
      mode="date" selectionType="single" autoApply usePortal
      openDirection="down" autoFlip labelType="none"
      inputVariant="compact"
      // The visible box is the picker's container, not its <input>, so the
      // height goes on inputClassName. Matches h-9 controls elsewhere; py-0
      // because the variant's own padding would add to it.
      inputClassName="h-9 py-0"
      // These fields always hold a date -- a cleared value is ignored here --
      // so a clear button would be a control that appears to do nothing.
      allowClear={false}
      className={className}
      ariaLabel={ariaLabel ?? label}
      value={asDate(value)}
      onChange={next => { if (next instanceof Date) onChange(asIso(next)); }}
    />
  );
  return label
    ? <label className="grid gap-1 text-sm font-medium">{label}{picker}</label>
    : picker;
}

export default IsoDatePicker;
